import { useContext, useState } from "react";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import MainSectionSection from "../../components/MainSectionSection";
import PipelineBlock, {
  type PipelineBlockSavePayload,
} from "../../components/pipeline/PipelineBlock";
import { trpc } from "../../trpc";
import MerchantContext from "../../contexts/MerchantContext";
import { hasTickets } from "@/utils/pipelines";
import LeadTagsManager from "@/components/leadTags/LeadTagsManager";

const ConfigurationContainer = () => {
  const { merchantId } = useContext(MerchantContext);
  const utils = trpc.useUtils();

  const { data: pipelines, isLoading } = trpc.pipeline.listFull.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const invalidatePipelines = () => {
    if (!merchantId) return;
    utils.pipeline.listFull.invalidate({ merchantId });
    utils.pipeline.get.invalidate({ merchantId });
    utils.pipeline.getPositions.invalidate();
  };

  const updatePipelineMutation = trpc.pipeline.updatePipeline.useMutation();

  const addPositionMutation = trpc.pipeline.addPosition.useMutation();
  const removePositionMutation = trpc.pipeline.removePosition.useMutation();
  const renamePositionMutation = trpc.pipeline.renamePosition.useMutation();
  const updatePositionHumanAssignmentMutation =
    trpc.pipeline.updatePositionHumanAssignment.useMutation();

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (input: PipelineBlockSavePayload) => {
    if (!merchantId || !pipelines) return;
    const pipeline = pipelines.find((p) => p.id === input.pipelineId);
    if (!pipeline) return;
    setIsSaving(true);
    try {
      await updatePipelineMutation.mutateAsync({
        merchantId,
        pipelineId: input.pipelineId,
        displayName: input.displayName,
        leadInactivityHours: input.leadInactivityHours,
        inactivityTransferTemperature: input.inactivityTransferTemperature,
        followUpConfig: input.followUpConfig,
      });
      const stages = ["pending", "success", "failure"] as const;
      type PositionWithHuman = {
        id: string;
        displayName: string;
        humanAssignmentEnabled?: boolean;
      };
      for (const stage of stages) {
        const original = (pipeline.positions[stage] ?? []) as PositionWithHuman[];
        const current = (input.positions[stage] ?? []) as (PositionWithHuman & {
          isNew?: boolean;
          humanAssignmentEnabled?: boolean;
        })[];
        const toRemove = original.filter((o) => !current.some((c) => c.id === o.id));
        const toRename = original.filter((o) => {
          const c = current.find((x) => x.id === o.id);
          return c && c.displayName !== o.displayName;
        });
        const toAdd = current.filter(
          (c) => (c as { isNew?: boolean }).isNew && c.displayName.trim()
        );
        for (const r of toRemove) {
          await removePositionMutation.mutateAsync({
            merchantId,
            pipelineId: input.pipelineId,
            positionId: r.id,
          });
        }
        for (const r of toRename) {
          const c = current.find((x) => x.id === r.id);
          if (c) {
            await renamePositionMutation.mutateAsync({
              merchantId,
              pipelineId: input.pipelineId,
              positionId: r.id,
              displayName: c.displayName,
            });
          }
        }
        for (const c of current) {
          if ((c as { isNew?: boolean }).isNew) continue;
          const o = original.find((x) => x.id === c.id);
          if (!o) continue;
          const currEnabled = c.humanAssignmentEnabled ?? false;
          const origEnabled = o.humanAssignmentEnabled ?? false;
          if (currEnabled !== origEnabled) {
            await updatePositionHumanAssignmentMutation.mutateAsync({
              merchantId,
              pipelineId: input.pipelineId,
              positionId: c.id,
              humanAssignmentEnabled: currEnabled,
            });
          }
        }
        for (const a of toAdd) {
          const status = a.id.trim() || a.displayName.toLowerCase().replace(/\s+/g, "_");
          await addPositionMutation.mutateAsync({
            merchantId,
            pipelineId: input.pipelineId,
            stage,
            status,
            displayName: a.displayName.trim(),
            positionId: a.id.trim() || undefined,
            humanAssignmentEnabled: (a as { humanAssignmentEnabled?: boolean })
              .humanAssignmentEnabled,
          });
        }
      }
      invalidatePipelines();
      toast.success("Proceso actualizado");
    } catch (err) {
      toast.error("Error al actualizar", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <MainSectionSection gap="24px">
        <div className="p-8 text-center">
          <Spinner size="lg" />
          <p className="mt-4">Cargando pipelines...</p>
        </div>
      </MainSectionSection>
    );
  }

  if (!pipelines?.length) {
    return (
      <MainSectionSection gap="24px">
        <div className="p-8 text-center">
          <p className="text-gray-600">No hay pipelines configurados.</p>
        </div>
      </MainSectionSection>
    );
  }

  return (
    <MainSectionSection gap="24px">
      <div>
        <h2 className="mb-4 text-xl font-bold">
          Procesos (ciclos de vida de las conversaciones)
        </h2>
        <p className="mb-6 text-sm text-gray-600">
          Cada proceso se muestra con su ID y nombre. Puedes editar el nombre para mostrar y agregar
          posiciones personalizadas en las etapas de Pendiente, Éxito y Fallido. También puedes
          modificar la configuración de mensajes de seguimiento y horas de inactividad.
        </p>
        <div className="flex flex-col gap-8">
          {pipelines.map((pipeline) => (
            <PipelineBlock
              key={pipeline.id}
              pipeline={pipeline}
              merchantId={merchantId || ""}
              onSave={handleSave}
              isSaving={isSaving}
              showTicketPositions={hasTickets(pipeline.id)}
            />
          ))}
        </div>
      </div>
      <LeadTagsManager />
    </MainSectionSection>
  );
};

export default ConfigurationContainer;
